import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { LoadingStateNoData } from '~/src/dash/components/dashboard/dashboard-graph-v2';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ModelDialogActiveView } from '~/src/dash/components/model-dialog-active-view';
import {
  defaultRowsPerPageOptions,
  filterActiveColumnKeys,
  IListViewTableColumnInfo,
  IListViewTableColumns,
  IListViewTableRow,
  IRowsPerPageOption,
  Order,
  translateRowsPerPageOptions,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ListViewTableContext } from '~/src/dash/components/tables/ListViewTable/context';
import { ListViewTableHead } from '~/src/dash/components/tables/ListViewTable/ListViewTableHead';
import { ListViewTablePagination } from '~/src/dash/components/tables/ListViewTable/ListViewTablePagination';
import { ListViewTableRow } from '~/src/dash/components/tables/ListViewTable/ListViewTableRow';
import { Div, TKey, usePublicaTranslation } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
} from '~/src/dash/utils/loading-states';

const getVisibleColumns = (columns: IListViewTableColumns, activeColumnKeys: string[]) => {
  const visibleColumns = [] as IListViewTableColumnInfo[];
  const { ID, Status, Name, Actions, CustomLeftColumn } = columns;
  visibleColumns.push(ID);
  if (Status) {
    visibleColumns.push(Status);
  }
  if (CustomLeftColumn) {
    visibleColumns.push(CustomLeftColumn);
  }
  visibleColumns.push(Name);
  activeColumnKeys.forEach((key) => {
    visibleColumns.push(columns[key]);
  });
  if (Actions) {
    visibleColumns.push(Actions);
  }
  return visibleColumns;
};

interface IListViewTableProps {
  activeColumnKeys: string[]; // keys of columns to be displayed
  backgroundColor?: string; // background color of entire table and cells
  className: string; // needed for cypress testing
  columns: IListViewTableColumns;
  customActiveMessage?: string;
  customDeleteMessage?: string;
  customFunctions?: any; // this is a bank of functions that will be sent out using the table's provider
  customProps?: any; // this is a bank of variables that will be sent out using the table's provider
  disableCurrencyConversion?: boolean; // used only in CurrencyRender and CurrencyRenderWithOtherCurrencies. set to true if values have already been converted
  disableDelete?: (row: IListViewTableRow) => boolean;
  disableDeleteTooltipMessageTKey?: TKey;
  displayNameWithID?: boolean; // true will display ID below Name
  deleteItemName?: string; // name of the type ofitem to be deleted
  enableActions?: boolean; // display actions column
  enableCustomLeftColumn?: boolean; // display custom column as the leftmost column of the table, after the select all checkbox and status switch
  enableStatusSwitch?: boolean; // display status column. must have accompanying IListViewTableColumn in columns
  enableSelectCheckbox?: boolean; // display bulk checkbox
  enableTrackingOpenObject?: boolean; // enable tracking when opening an object
  getItemSettingsRoute?: (id: string) => string; // use to create path for item settings view
  handleChangePage: (e: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCloneItem?: (id: string) => void;
  handleDeleteItem?: (id: string, idx?: number) => Promise<any>;
  handleOnChangeStatus?: (id: string, checked: boolean, row: IListViewTableRow) => void; // function to change status active / inactive
  handleOnChangeSelect?: (id: string, row: IListViewTableRow) => void; // function for select checkbox if enabled
  handleOnClickColumnHeader: (column: IListViewTableColumnInfo) => void; // in most cases this will be your click to sort
  handleOnClickRow?: (row: any) => void;
  handleSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void; // if you enabled select checkbox, you need to pass a function
  hasActiveSwitchSafeguard?: boolean; // if true, will show a modal before changing the status of an item
  isActionPending?: boolean; // when true, disables status switch / action buttons, while an api action is awaiting success
  isExpanded?: boolean; // table padding
  isReadAuthorized: boolean;
  isSuperUser?: boolean;
  isWriteAuthorized: boolean;
  loadState: LoadingState;
  order: Order;
  orderBy: string;
  page: number;
  rows: IListViewTableRow[];
  rowsPerPage?: number;
  rowsPerPageOptions?: IRowsPerPageOption[]; // make sure this works with rowsPerPage above
  selected?: string[];
  tableName?: string; // used for tracking open object link
  totalRowCount: number;
}

export const ListViewTable = ({
  activeColumnKeys,
  backgroundColor = publicaColors.white,
  className,
  columns,
  customActiveMessage = '',
  customDeleteMessage = '',
  customFunctions,
  customProps,
  disableCurrencyConversion = false,
  disableDelete = () => false,
  disableDeleteTooltipMessageTKey,
  displayNameWithID,
  deleteItemName,
  enableActions,
  enableCustomLeftColumn,
  enableStatusSwitch,
  enableSelectCheckbox,
  enableTrackingOpenObject = false,
  getItemSettingsRoute,
  handleChangePage,
  handleChangeRowsPerPage,
  handleDeleteItem,
  handleOnChangeStatus,
  handleOnChangeSelect,
  handleOnClickColumnHeader,
  handleOnClickRow,
  handleCloneItem,
  handleSelectAllClick,
  hasActiveSwitchSafeguard,
  isActionPending,
  isExpanded,
  isReadAuthorized,
  isSuperUser = false,
  isWriteAuthorized,
  loadState,
  order = 'asc',
  orderBy = 'Name',
  page,
  rows,
  rowsPerPage = 10,
  rowsPerPageOptions,
  selected = [] as string[],
  tableName,
  totalRowCount,
}: IListViewTableProps) => {
  const { t } = usePublicaTranslation();
  // load status
  const isUpdating = loadState === LoadingStateLoading;
  const isLoaded = loadState === LoadingStateHasData;
  const isDataEmpty = loadState === LoadingStateNoData;

  // states
  // delete modal params
  const [isDeleteModalActive, setIsDeleteModalActive] = useState(false);
  const [deleteModalID, setDeleteModalID] = useState<string>('');
  const [deleteModalName, setDeleteModalName] = useState<string>('');

  // active modal params
  const [isActiveModalOpen, setIsActiveModalOpen] = useState(false);
  const [activeModalID, setActiveModalID] = useState<string>('');
  const [activeModalName, setActiveModalName] = useState<string>('');
  const [activeModalUpdateParams, setActiveModalUpdateParams] = useState<{
    id: string;
    checked: boolean;
    row: IListViewTableRow;
  } | null>(null);

  const filteredActiveColumnKeys = filterActiveColumnKeys(activeColumnKeys, columns, isSuperUser);
  const visibleColumns = getVisibleColumns(columns, filteredActiveColumnKeys);
  const totalCols = enableSelectCheckbox ? visibleColumns.length : visibleColumns.length - 1;

  // handles
  const handleCloseDeleteDialog = (): void => {
    unstable_batchedUpdates(() => {
      setIsDeleteModalActive(false);
      setDeleteModalID('');
      setDeleteModalName('');
    });
  };

  const handleCloseActiveDialog = (): void => {
    unstable_batchedUpdates(() => {
      setIsActiveModalOpen(false);
      setActiveModalID('');
      setActiveModalName('');
    });
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const numSelected = selected.length;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - totalRowCount) : 0;

  return (
    <ListViewTableContext.Provider
      value={{
        customFunctions,
        customProps,
        isActionPending: isActionPending || false,
        isCurrencyConversionDisabled: disableCurrencyConversion,
        isReadAuthorized,
        isUpdating,
        isWriteAuthorized,
        setDeleteModalID,
        setDeleteModalName,
        setIsDeleteModalActive,
        setActiveModalID,
        setActiveModalName,
        setIsActiveModalOpen,
      }}
    >
      <Div>
        <TableContainer>
          <Table
            aria-label={`${className}-table`}
            size={isExpanded ? 'medium' : 'small'}
            className={`cypress-${className}-table`}
          >
            <ListViewTableHead
              backgroundColor={backgroundColor}
              className={className}
              enableActions={enableActions}
              enableSelectCheckbox={enableSelectCheckbox}
              enableStatusSwitch={enableStatusSwitch}
              numSelected={numSelected}
              onClickColumnHeader={handleOnClickColumnHeader}
              onSelectAllClick={handleSelectAllClick}
              order={order}
              orderBy={orderBy}
              visibleColumns={visibleColumns}
              visibleRowCount={rows.length}
            />
            <TableBody
              style={{
                borderBottom: '1px solid rgb(224, 224, 224)',
              }}
            >
              {(isDataEmpty || isEmpty(rows)) && (
                <TableRow style={{ height: isUpdating ? 'auto' : '96px' }}>
                  <TableCell
                    colSpan={totalCols}
                    style={{
                      textAlign: 'center',
                      verticalAlign: 'middle',
                    }}
                  >
                    {isUpdating ? <Skeleton variant='text' animation='wave' /> : t('NoDataFound')}
                  </TableCell>
                </TableRow>
              )}
              {isReadAuthorized &&
                (isLoaded || isUpdating) &&
                !isEmpty(rows) &&
                rows.map((row) => {
                  const id = row.ID.value;
                  let isItemSelected = isSelected(id as string);

                  return (
                    <ListViewTableRow
                      activeColumnKeys={filteredActiveColumnKeys}
                      backgroundColor={backgroundColor}
                      className={className}
                      disableDelete={disableDelete}
                      disableDeleteTooltipMessageTKey={disableDeleteTooltipMessageTKey}
                      displayNameWithID={displayNameWithID}
                      enableActions={enableActions}
                      enableClone={!!handleCloneItem}
                      enableCustomLeftColumn={enableCustomLeftColumn}
                      enableSelectCheckbox={enableSelectCheckbox}
                      enableStatusSwitch={enableStatusSwitch}
                      getItemSettingsRoute={getItemSettingsRoute}
                      handleClick={handleOnClickRow}
                      handleCloneItem={handleCloneItem}
                      enableTrackingOpenObject={enableTrackingOpenObject}
                      tableName={tableName}
                      handleOnChangeSelect={handleOnChangeSelect}
                      handleOnChangeStatus={handleOnChangeStatus}
                      hasActiveSwitchSafeguard={hasActiveSwitchSafeguard}
                      isActionPending={isActionPending}
                      isExpanded={isExpanded}
                      isItemSelected={isItemSelected}
                      isUpdating={isUpdating}
                      isWriteAuthorized={isWriteAuthorized}
                      row={row}
                      setActiveModalUpdateParams={setActiveModalUpdateParams}
                      showDelete={!!handleDeleteItem}
                    />
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (isExpanded ? 53 : 33) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={totalRowCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={ListViewTablePagination}
          labelRowsPerPage=''
          rowsPerPageOptions={translateRowsPerPageOptions(
            rowsPerPageOptions || defaultRowsPerPageOptions,
            t,
          )}
        />
        <ModelDialogDeleteView
          active={isDeleteModalActive}
          ID={deleteModalID}
          model={deleteItemName}
          Name={deleteModalName}
          deleteModel={(id: string, idx?: number): Promise<any> => {
            if (handleDeleteItem) {
              return handleDeleteItem(id, idx);
            }
            return Promise.resolve();
          }}
          onDelete={handleCloseDeleteDialog}
          onError={handleCloseDeleteDialog}
          onClose={handleCloseDeleteDialog}
          customMessage={customDeleteMessage}
        />
        <ModelDialogActiveView
          active={isActiveModalOpen}
          ID={activeModalID}
          currentlyActive={!activeModalUpdateParams?.checked}
          name={activeModalName}
          activeModel={(): Promise<unknown> => {
            if (handleOnChangeStatus && activeModalUpdateParams) {
              handleOnChangeStatus(
                activeModalUpdateParams.id,
                activeModalUpdateParams.checked,
                activeModalUpdateParams.row,
              );
            }
            return Promise.resolve();
          }}
          onError={handleCloseActiveDialog}
          onClose={handleCloseActiveDialog}
          customMessage={customActiveMessage}
        />
      </Div>
    </ListViewTableContext.Provider>
  );
};
